import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import { VideoFeedback } from '@/store/videoFeatures';
import { CheckCircle2 } from 'lucide-react';

interface Props {
  config: VideoFeedback;
}

const EMOJIS = ['😡', '😕', '😐', '🙂', '😄'];
const EMOJI_LABELS = ['Muito insatisfeito', 'Insatisfeito', 'Neutro', 'Satisfeito', 'Muito satisfeito'];

export const VideoFeedbackPreview: React.FC<Props> = ({ config }) => {
  useLanguage();
  const [selected, setSelected] = useState<number | null>(null);
  const [stage, setStage] = useState<'rate' | 'comment' | 'identify' | 'thanks'>('rate');
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [ddd, setDdd] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    setSelected(null);
    setStage('rate');
    setComment('');
    setName('');
    setEmail('');
    setDdd('');
    setPhone('');
  }, [config.ratingType]);

  const isDark = config.appearance.style === 'glass-dark';
  const isMinimal = config.appearance.style === 'minimal';

  const cardStyle: React.CSSProperties = {
    background: isDark
      ? 'rgba(23, 23, 23, 0.78)'
      : isMinimal
      ? 'rgba(255,255,255,0.95)'
      : '#FFFFFF',
    color: isDark ? '#FFFFFF' : '#1A1A1A',
    borderColor: isDark ? 'rgba(255,255,255,0.14)' : '#E5EAF3',
    backdropFilter: isDark ? 'blur(16px)' : isMinimal ? 'blur(6px)' : undefined,
    WebkitBackdropFilter: isDark ? 'blur(16px)' : isMinimal ? 'blur(6px)' : undefined,
  };

  const widthBySize =
    config.appearance.size === 'compact' ? 360 : config.appearance.size === 'large' ? 520 : 460;

  const positionClass =
    config.appearance.position === 'center'
      ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
      : config.appearance.position === 'bottom-center'
      ? 'bottom-6 left-1/2 -translate-x-1/2'
      : 'bottom-6 right-6';

  const subtleText = isDark ? 'rgba(255,255,255,0.72)' : '#6B7280';

  const needsIdent = config.identification !== 'none' && config.identification !== 'use_lead';

  const handleSubmit = () => {
    if (config.comment.enabled && stage === 'rate') {
      setStage('comment');
      return;
    }
    if (needsIdent && stage !== 'identify') {
      setStage('identify');
      return;
    }
    setStage('thanks');
  };

  const handleReset = () => {
    setSelected(null);
    setStage('rate');
    setComment('');
    setName('');
    setEmail('');
    setDdd('');
    setPhone('');
  };

  const buttonDisabled = selected === null;
  const buttonLabel = buttonDisabled ? translateInterfaceText("Selecione uma avaliação") : config.buttonText;

  const inputStyle: React.CSSProperties = {
    background: isDark ? 'rgba(255,255,255,0.08)' : '#F8FAFC',
    color: isDark ? '#FFFFFF' : '#1A1A1A',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#E5E7EB'}`,
  };

  const renderRatingControl = () => {
    if (config.ratingType === 'emoji') {
      return (
        <div className="flex items-center justify-center gap-2 mb-5">
          {EMOJIS.map((e, i) => {
            const active = selected === i + 1;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setSelected(i + 1)}
                aria-label={EMOJI_LABELS[i]}
                className="text-[28px] rounded-full w-11 h-11 flex items-center justify-center transition-all"
                style={{
                  background: active ? (isDark ? 'rgba(75,123,255,0.25)' : '#EEF2FF') : 'transparent',
                  transform: active ? 'scale(1.08)' : 'scale(1)',
                }}
              >
                {e}
              </button>
            );
          })}
        </div>
      );
    }

    if (config.ratingType === 'stars') {
      return (
        <div className="flex items-center justify-center gap-1.5 mb-5">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = selected !== null && n <= selected;
            return (
              <button
                key={n}
                type="button"
                onClick={() => setSelected(n)}
                className="w-10 h-10 flex items-center justify-center text-[28px] transition-transform"
                style={{ color: active ? '#F5B400' : isDark ? 'rgba(255,255,255,0.45)' : '#CBD5E1' }}
              >
                ★
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center flex-wrap gap-1.5 mb-5">
        {Array.from({ length: 5 }, (_, i) => {
          const n = i + 1;
          const active = selected === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setSelected((prev) => (prev === n ? null : n))}
              className="w-8 h-8 rounded-full text-[12px] font-semibold transition-colors border"
              style={{
                background: active ? config.appearance.color : 'transparent',
                borderColor: active ? config.appearance.color : isDark ? 'rgba(255,255,255,0.25)' : '#E5E7EB',
                color: active ? '#FFFFFF' : isDark ? 'rgba(255,255,255,0.85)' : '#1A1A1A',
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div data-theme-isolated
      className={`absolute z-30 pointer-events-auto ${positionClass}`}
      style={{
        width: `min(${widthBySize}px, calc(100% - 32px))`,
        maxWidth: 'calc(100% - 32px)',
      }}
    >
      <div className="rounded-[20px] border shadow-2xl p-6 text-center" style={cardStyle}>
        {stage === 'rate' && (
          <>
            <h3 className="text-[15px] font-semibold mb-1">{config.title}</h3>
            {config.subtitle && (
              <p className="text-[12.5px] mb-4" style={{ color: subtleText }}>
                {config.subtitle}
              </p>
            )}
            {renderRatingControl()}
            <button
              type="button"
              disabled={buttonDisabled}
              onClick={handleSubmit}
              className="w-full py-2.5 rounded-[10px] text-[13px] font-semibold transition-opacity"
              style={{
                background: buttonDisabled ? `${config.appearance.color}33` : config.appearance.color,
                color: buttonDisabled ? (isDark ? 'rgba(255,255,255,0.7)' : '#FFFFFF') : '#FFFFFF',
                cursor: buttonDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {buttonLabel}
            </button>
          </>
        )}

        {stage === 'comment' && (
          <>
            <h3 className="text-[15px] font-semibold mb-3">{translateInterfaceText("Conte um pouco mais")}</h3>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={config.comment.placeholder}
              rows={3}
              className="w-full rounded-[10px] p-3 text-[13px] resize-none mb-4 outline-none"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full py-2.5 rounded-[10px] text-[13px] font-semibold"
              style={{ background: config.appearance.color, color: '#FFFFFF' }}
            >
              {config.buttonText}
            </button>
          </>
        )}

        {stage === 'identify' && (
          <>
            <h3 className="text-[15px] font-semibold mb-1">{translateInterfaceText("Para finalizar")}</h3>
            <p className="text-[12.5px] mb-4" style={{ color: subtleText }}>
              {translateInterfaceText("Precisamos de alguns dados para registrar sua resposta.")}</p>
            <div className="space-y-2.5 text-left mb-4">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={translateInterfaceText("Seu nome")}
                className="w-full rounded-[10px] px-3 py-2.5 text-[13px] outline-none"
                style={inputStyle}
              />
              {(config.identification === 'name_email' || config.identification === 'name_email_whatsapp') && (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="w-full rounded-[10px] px-3 py-2.5 text-[13px] outline-none"
                  style={inputStyle}
                />
              )}
              {config.identification === 'name_email_whatsapp' && (
                <div className="flex gap-1.5 w-full">
                  <div
                    className="rounded-[10px] px-2 py-2.5 text-[13px] flex items-center justify-center gap-1 w-[68px] shrink-0 select-none"
                    style={inputStyle}
                  >
                    <span>🇧🇷</span>
                    <span className="font-medium">+55</span>
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={ddd}
                    onChange={(e) => setDdd(e.target.value.replace(/\D/g, '').slice(0, 3))}
                    placeholder={translateInterfaceText("DDD")}
                    className="rounded-[10px] px-2 py-2.5 text-[13px] outline-none w-[56px] shrink-0 text-center"
                    style={inputStyle}
                  />
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder={translateInterfaceText("Número")}
                    className="flex-1 min-w-0 rounded-[10px] px-3 py-2.5 text-[13px] outline-none"
                    style={inputStyle}
                  />
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full py-2.5 rounded-[10px] text-[13px] font-semibold"
              style={{ background: config.appearance.color, color: '#FFFFFF' }}
            >
              {config.buttonText}
            </button>
          </>
        )}

        {stage === 'thanks' && (
          <div className="flex flex-col items-center py-2">
            <CheckCircle2 className="w-10 h-10 mb-3" style={{ color: config.appearance.color }} strokeWidth={1.8} />
            <h3 className="text-[15px] font-semibold mb-1">{config.thanksMessage}</h3>
            <p className="text-[12.5px]" style={{ color: subtleText }}>
              {translateInterfaceText("Sua resposta foi registrada com sucesso.")}</p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-3 text-[12px] underline-offset-2 hover:underline"
              style={{ color: subtleText }}
            >
              {translateInterfaceText("Reiniciar preview")}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoFeedbackPreview;
